import type { EnvironmentProviders } from '@angular/core';
import { importProvidersFrom } from '@angular/core';
import { LucideAngularModule } from 'lucide-angular';
import {
  AlertCircle,
  Archive,
  ArrowDown,
  ArrowLeftRight,
  ArrowRight,
  ArrowUp,
  ArrowUpRight,
  AtSign,
  Bell,
  Building2,
  Calendar,
  Camera,
  Check,
  CheckCircle2,
  CheckCircle,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  ClipboardList,
  Clock,
  Copy,
  Download,
  Eye,
  EyeOff,
  File,
  Layers,
  FileText,
  Filter,
  Folder,
  Info,
  LayoutDashboard,
  Link2,
  List,
  Loader2,
  LogOut,
  Mail,
  MapPin,
  MessageSquare,
  Monitor,
  MoreVertical,
  Paperclip,
  Pencil,
  Phone,
  Plus,
  RefreshCcw,
  Route,
  Save,
  Search,
  Send,
  Settings,
  Ship,
  Trash2,
  TrendingDown,
  TrendingUp,
  Truck,
  Upload,
  User,
  Users,
  X,
} from 'lucide-angular';

/**
 * Curated icon set for the EBT apps — these are the names the
 * `<ebt-icon>` component knows about. Lucide ships ~1,000 icons but
 * bundling all of them would bloat the apps; we register just what the
 * UI uses today and add more when a feature needs them.
 *
 * Naming: pass these as kebab-case strings to `<ebt-icon name="arrow-right" />`
 * — the wrapper component handles the mapping.
 */
export const EBT_ICONS = {
  'alert-circle': AlertCircle,
  archive: Archive,
  'arrow-down': ArrowDown,
  'arrow-left-right': ArrowLeftRight,
  'arrow-right': ArrowRight,
  'arrow-up': ArrowUp,
  'arrow-up-right': ArrowUpRight,
  'at-sign': AtSign,
  bell: Bell,
  building: Building2,
  calendar: Calendar,
  camera: Camera,
  check: Check,
  'check-circle': CheckCircle2,
  'check-circle-thin': CheckCircle,
  'chevron-down': ChevronDown,
  'chevron-left': ChevronLeft,
  'chevron-right': ChevronRight,
  'chevron-up': ChevronUp,
  'clipboard-list': ClipboardList,
  clock: Clock,
  copy: Copy,
  download: Download,
  eye: Eye,
  'eye-off': EyeOff,
  file: File,
  'file-text': FileText,
  filter: Filter,
  folder: Folder,
  info: Info,
  layers: Layers,
  'layout-dashboard': LayoutDashboard,
  link: Link2,
  list: List,
  loader: Loader2,
  'log-out': LogOut,
  mail: Mail,
  'map-pin': MapPin,
  message: MessageSquare,
  monitor: Monitor,
  'more-vertical': MoreVertical,
  paperclip: Paperclip,
  pencil: Pencil,
  phone: Phone,
  plus: Plus,
  refresh: RefreshCcw,
  route: Route,
  save: Save,
  search: Search,
  send: Send,
  settings: Settings,
  ship: Ship,
  trash: Trash2,
  'trending-down': TrendingDown,
  'trending-up': TrendingUp,
  truck: Truck,
  upload: Upload,
  user: User,
  users: Users,
  close: X,
} as const;

/** Name union for `<ebt-icon name="...">`. */
export type EbtIconName = keyof typeof EBT_ICONS;

/**
 * Register the EBT icon set with lucide-angular's provider. Apps wire
 * this into their `app.config.ts`:
 *
 *   import { provideEbtIcons } from '@ebt/shared-ui';
 *   ...
 *   providers: [provideEbtIcons()]
 *
 * Implementation detail: `lucide-angular` looks up icons via
 * `toPascalCase(name)` against the keys provided to
 * `LucideAngularModule.pick(...)`. That means a kebab-case-keyed map
 * (the API we want consumers to see — `<ebt-icon name="layout-dashboard" />`)
 * silently fails the lookup. We translate the keys to PascalCase
 * here so kebab-case stays the public surface while lucide's
 * internal lookup finds them.
 */
export function provideEbtIcons(): EnvironmentProviders {
  const pascalCased: Record<string, unknown> = {};
  for (const [kebab, icon] of Object.entries(EBT_ICONS)) {
    const pascal = kebab
      .split('-')
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
      .join('');
    pascalCased[pascal] = icon;
  }
  return importProvidersFrom(LucideAngularModule.pick(pascalCased as never));
}
